import { useEffect, useState } from 'react'
import { TranslationProgress } from '../utils/constants'
import { useLanguages, convertToTranslationFormat } from '../hooks/useLanguages'
import { useTranslation } from '../i18n/hooks/useTranslation'

interface TranslationProgressModalProps {
  isOpen: boolean
  progress: Record<string, TranslationProgress>
  onClose: () => void
  title?: string
  categories?: Array<{ id: string; name: string }>
}

export function TranslationProgressModal({ 
  isOpen, 
  progress, 
  onClose, 
  title,
  categories = []
}: TranslationProgressModalProps) {
  const { t } = useTranslation()
  const [startTime, setStartTime] = useState<number | null>(null)
  const [currentTime, setCurrentTime] = useState(Date.now())
  
  // Use dynamic languages from database
  const { languages: dynamicLanguages } = useLanguages()
  const SUPPORTED_LANGUAGES = convertToTranslationFormat(dynamicLanguages)

  // Update current time every second for elapsed time display
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Set start time when first translation begins
  useEffect(() => {
    const hasStarted = Object.values(progress).some(p => p.status === 'translating' || p.status === 'completed')
    if (hasStarted && !startTime) {
      setStartTime(Date.now())
    }
  }, [progress, startTime])

  if (!isOpen) return null

  const progressEntries = Object.entries(progress)
  const totalTranslations = progressEntries.length
  const completedTranslations = progressEntries.filter(([_, p]) => p.status === 'completed').length
  const savingTranslations = progressEntries.filter(([_, p]) => p.status === 'saving').length
  const savedTranslations = progressEntries.filter(([_, p]) => p.status === 'saved').length
  const failedTranslations = progressEntries.filter(([_, p]) => p.status === 'error').length
  const inProgressTranslations = progressEntries.filter(([_, p]) => p.status === 'translating').length
  const pendingTranslations = progressEntries.filter(([_, p]) => p.status === 'pending').length

  const isTranslating = inProgressTranslations > 0 || pendingTranslations > 0 || savingTranslations > 0
  const allCompleted = savedTranslations + failedTranslations === totalTranslations

  const elapsedSeconds = startTime ? Math.floor((currentTime - startTime) / 1000) : 0
  
  const getProgressColor = (status: TranslationProgress['status']) => {
    switch (status) {
      case 'completed': return 'text-blue-600 bg-blue-100'
      case 'saving': return 'text-gray-600 bg-gray-100'
      case 'saved': return 'text-green-600 bg-green-100'
      case 'translating': return 'text-indigo-600 bg-indigo-100'
      case 'error': return 'text-red-600 bg-red-100'
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusIcon = (status: TranslationProgress['status']) => {
    switch (status) {
      case 'completed': return '✅'
      case 'saving': return '💽'
      case 'saved': return '💾'
      case 'translating': return '⏳'
      case 'error': return '❌'
      case 'pending': return '⏱️'
      default: return '❓'
    }
  }

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const getCategoryName = (key: string) => {
    if (categories.length > 0) {
      // For bulk translations: key format is "categoryId-language"
      const [categoryId] = key.split('-')
      const category = categories.find(c => c.id === categoryId)
      return category?.name || categoryId
    }
    // For single category translations: key is just the language
    return 'Category'
  }

  const getLanguageName = (key: string) => {
    let languageCode: string
    if (categories.length > 0) {
      // For bulk translations: key format is "categoryId-language"
      const [, language] = key.split('-')
      languageCode = language
    } else {
      // For single category translations: key is just the language
      languageCode = key
    }
    
    const language = SUPPORTED_LANGUAGES.find(l => l.code === languageCode)
    return language ? `${language.flag} ${language.name}` : languageCode
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <span className="mr-2">🤖</span>
                {title || t('translations.progress.title')}
              </h2>
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                <span>{t('translations.progress.total')}: {totalTranslations}</span>
                <span className="text-green-600">💾 {savedTranslations}</span>
                {savingTranslations > 0 && (
                  <span className="text-gray-600">💽 {savingTranslations}</span>
                )}
                {completedTranslations > 0 && (
                  <span className="text-blue-600">✅ {completedTranslations}</span>
                )}
                <span className="text-indigo-600">⏳ {inProgressTranslations}</span>
                <span className="text-yellow-600">⏱️ {pendingTranslations}</span>
                {failedTranslations > 0 && (
                  <span className="text-red-600">❌ {failedTranslations}</span>
                )}
              </div>
            </div>
            
            {/* Close button - only enabled when all translations are done */}
            <button
              onClick={onClose}
              disabled={isTranslating}
              className={`text-gray-400 hover:text-gray-600 p-2 rounded-full transition-colors ${
                isTranslating 
                  ? 'cursor-not-allowed opacity-50' 
                  : 'hover:bg-gray-100'
              }`}
              title={isTranslating ? t('translations.progress.cannotClose') : t('translations.progress.close')}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t('translations.progress.progress')}: {savedTranslations}/{totalTranslations} {t('translations.progress.saved').toLowerCase()}
            </span>
            <span className="text-sm text-gray-600">
              {startTime && `${t('translations.progress.elapsed')}: ${formatTime(elapsedSeconds)}`}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-blue-500 to-green-500 h-3 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${totalTranslations > 0 ? (savedTranslations / totalTranslations) * 100 : 0}%` }}
            />
          </div>
          {allCompleted && (
            <div className="mt-2 text-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                {t('translations.progress.allCompleted')}
              </span>
            </div>
          )}
        </div>

        {/* Translation List */}
        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-3">
            {progressEntries.map(([key, translationProgress]) => {
              const categoryName = getCategoryName(key)
              const languageName = getLanguageName(key)
              const duration = translationProgress.startTime && translationProgress.endTime 
                ? Math.round((translationProgress.endTime - translationProgress.startTime) / 1000)
                : null

              return (
                <div 
                  key={key} 
                  className={`p-4 rounded-lg border transition-all duration-300 ${
                    translationProgress.status === 'translating' 
                      ? 'border-blue-300 bg-blue-50 shadow-md' 
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">
                          {getStatusIcon(translationProgress.status)}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900">
                            {categoryName} → {languageName}
                          </div>
                          <div className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${getProgressColor(translationProgress.status)}`}>
                            {translationProgress.status.charAt(0).toUpperCase() + translationProgress.status.slice(1)}
                          </div>
                        </div>
                      </div>
                      
                      {translationProgress.error && (
                        <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                          Error: {translationProgress.error}
                        </div>
                      )}
                    </div>

                    <div className="text-right text-sm text-gray-500">
                      {translationProgress.status === 'translating' && (
                        <div className="flex items-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                          <span>{t('translations.progress.translatingInProgress')}</span>
                        </div>
                      )}
                      {translationProgress.status === 'saving' && (
                        <div className="flex items-center space-x-2">
                          <div className="animate-pulse rounded-full h-4 w-4 bg-gray-600"></div>
                          <span>{t('translations.progress.savingInProgress')}</span>
                        </div>
                      )}
                      {translationProgress.status === 'completed' && duration && (
                        <span className="text-blue-600 font-medium">
                          ⚡ {duration}s
                        </span>
                      )}
                      {translationProgress.status === 'saved' && duration && (
                        <span className="text-green-600 font-medium">
                          ✨ {duration}s
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {isTranslating ? (
                <span className="flex items-center">
                  <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                  {t('translations.progress.aiWorking')}
                </span>
              ) : allCompleted ? (
                <span className="text-green-600 font-medium">
                  {t('translations.progress.allCompletedSuccess')}
                </span>
              ) : (
                <span>{t('translations.progress.processReady')}</span>
              )}
            </div>
            
            <button
              onClick={onClose}
              disabled={isTranslating}
              className={`px-4 py-2 rounded-full font-medium transition-colors ${
                isTranslating
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              {isTranslating ? t('translations.progress.translatingInProgress') : t('translations.progress.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 